// src/components/PostDetail.js
// Fullscreen Drift post view: media, creator, like, remix, share (copy link),
// and comments. Fires a view on open. Used by the Drift feed + creator pages.

import { useEffect, useRef, useState } from "react";
import Avatar from "./Avatar";
import { drift } from "../api";

export default function PostDetail({ post, user, onClose, onRequireAuth, onRemix, onLike, onOpenCreator, onDeleted }) {
  const canDelete = user && String(post.creator_id) === String(user.id);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [copied, setCopied] = useState(false);
  const openedAt = useRef(Date.now());

  useEffect(() => {
    drift.comments(post._id).then((d) => setComments(d.comments || [])).catch(() => {});
    // fire a view; report watch time when the detail closes
    drift.view(post._id, 0).catch(() => {});
    const started = openedAt.current;
    return () => {
      const secs = Math.round((Date.now() - started) / 1000);
      if (secs > 0) drift.view(post._id, secs).catch(() => {});
    };
  }, [post._id]);

  const addComment = async () => {
    if (!user) return onRequireAuth();
    const t = text.trim();
    if (!t || posting) return;
    setPosting(true);
    try {
      const r = await drift.comment(post._id, t);
      setComments((c) => [...c, r.comment]);
      setText("");
    } catch (_) {} finally {
      setPosting(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this drift permanently?")) return;
    try {
      await drift.remove(post._id);
      onDeleted?.(post);
      onClose();
    } catch (e) {
      alert(e.message);
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/drift/${post._id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {}
    drift.trackShare(post._id).catch(() => {});
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="viewer viewer--wide" onClick={(e) => e.stopPropagation()}>
        <button className="viewer__close" onClick={onClose}>✕</button>

        <div className="viewer__media-wrap">
          {post.type === "video" ? (
            <video className="viewer__media" src={post.media_url} controls autoPlay loop />
          ) : (
            <img className="viewer__media" src={post.media_url} alt={post.prompt} />
          )}
        </div>

        <div className="viewer__side">
          <div className="viewer__creator" onClick={() => onOpenCreator?.(post.creator_id)}>
            <Avatar name={post.creator_username} url={post.creator_avatar_url} size={28} />
            <span>@{post.creator_username}</span>
          </div>

          {post.prompt && <p className="viewer__prompt">{post.prompt}</p>}

          <div className="viewer__actions">
            <button className="btn btn--ghost" onClick={() => onLike(post)}>♥ {post.likes_count ?? post.likes ?? 0}</button>
            <button className="btn btn--ghost" onClick={share}>{copied ? "Copied!" : "↗ Share"}</button>
            <button className="btn btn--primary" onClick={() => onRemix(post)}>Remix</button>
            {canDelete && <button className="btn btn--danger" onClick={del}>Delete</button>}
          </div>

          <div className="comments">
            <div className="comments__title">Comments ({comments.length})</div>
            <div className="comments__list">
              {comments.length === 0 ? (
                <div className="comments__empty">No comments yet.</div>
              ) : (
                comments.map((c) => (
                  <div key={c._id || c.created_at} className="comment">
                    <Avatar name={c.creator_username || "user"} url={c.creator_avatar_url} size={24} />
                    <div>
                      <span className="comment__user">@{c.creator_username}</span>
                      <span className="comment__text">{c.text}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="comments__compose">
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addComment()}
                placeholder={user ? "Add a comment…" : "Log in to comment"}
              />
              <button className="btn btn--primary" onClick={addComment} disabled={posting || !text.trim()}>Post</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
